import { router, useForm } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft } from 'lucide-react';

interface Recommendation {
  id?: string;
  workflow_function_name?: string;
  use_cases?: string;
  keywords?: string[];
  active?: boolean;
}

interface FormProps {
  recommendation: Recommendation | null;
  errors?: string[];
}

export default function Form({ recommendation, errors }: FormProps) {
  const isEditing = !!recommendation?.id;

  const { data, setData, post, put, processing } = useForm({
    workflow_function_name: recommendation?.workflow_function_name || '',
    use_cases: recommendation?.use_cases || '',
    keywords: recommendation?.keywords?.join(', ') || '',
    active: recommendation?.active ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const formData = {
      chat_recommendation: {
        workflow_function_name: data.workflow_function_name,
        use_cases: data.use_cases,
        keywords: data.keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
        active: data.active,
      },
    };

    if (isEditing) {
      put(`/dev/chat_recommendations/${recommendation.id}`, formData);
    } else {
      post('/dev/chat_recommendations', formData);
    }
  };

  const handleBack = () => {
    router.visit('/dev/chat_recommendations');
  };

  return (
    <div className="container mx-auto p-8 max-w-2xl">
      <Button variant="ghost" onClick={handleBack} className="mb-6">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to recommendations
      </Button>

      <h1 className="text-2xl font-semibold mb-6">
        {isEditing ? 'Edit Recommendation' : 'New Recommendation'}
      </h1>

      {errors && errors.length > 0 && (
        <div className="bg-destructive/10 text-destructive rounded-md p-4 mb-6">
          <ul className="list-disc list-inside">
            {errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="workflow_function_name">Workflow Function Name</Label>
          <Input
            id="workflow_function_name"
            value={data.workflow_function_name}
            onChange={(e) => setData('workflow_function_name', e.target.value)}
            placeholder="e.g., contentUtilsImageNanoGeneralWorkflow"
            disabled={isEditing}
          />
          <p className="text-sm text-muted-foreground">
            The exact function name of the workflow. Find this in the workflow catalog.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="use_cases">Use Cases</Label>
          <Textarea
            id="use_cases"
            value={data.use_cases}
            onChange={(e) => setData('use_cases', e.target.value)}
            placeholder="Describe when to use this workflow. e.g., 'Perfect for creating blog post headers, social media graphics, and marketing visuals. Use when users ask for images, illustrations, or visual content.'"
            rows={4}
          />
          <p className="text-sm text-muted-foreground">
            Describe when the chat assistant should recommend this workflow. Be specific about use
            cases.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="keywords">Keywords</Label>
          <Input
            id="keywords"
            value={data.keywords}
            onChange={(e) => setData('keywords', e.target.value)}
            placeholder="e.g., images, blog, graphics, visual, illustration"
          />
          <p className="text-sm text-muted-foreground">
            Comma-separated keywords that should trigger this recommendation in search.
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="active"
            checked={data.active}
            onCheckedChange={(checked) => setData('active', checked)}
          />
          <Label htmlFor="active">Active</Label>
        </div>

        <div className="flex gap-4">
          <Button type="submit" disabled={processing}>
            {isEditing ? 'Update' : 'Create'} Recommendation
          </Button>
          <Button type="button" variant="outline" onClick={handleBack}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
